import { format, isWithinInterval, parseISO } from 'date-fns';
import type { DashboardSummary } from '../../services/api';
import type { Employee, HolidayRequest, PayRecord, Timecard, TimecardDay } from '../../types';
import { resolveWorkHourlyRate } from '../../utils/laborCost';
import { computeLateAndEarlyMinutes } from '../../utils/earlyLateReport';
import { getActualPaidHours, getScheduledHours } from '../../utils/overviewDashboardMetrics';
import type { EmployeeStatItem } from './types';

export interface EmployeeReportDetailSheet {
  /** Matches the summary metric label */
  metricLabel: string;
  title: string;
  headers: string[];
  rows: string[][];
}

export interface IndividualEmployeeExportPayload {
  employeeName: string;
  role: string;
  periodLabel: string;
  fromDate: string;
  toDate: string;
  summaryMetrics: EmployeeStatItem[];
  detailSheets: EmployeeReportDetailSheet[];
}

function formatShortDate(value: string | undefined | null): string {
  if (!value) return '';
  try {
    return format(parseISO(value.slice(0, 10)), 'd MMM yyyy');
  } catch {
    return value;
  }
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  try {
    return format(parseISO(value), 'HH:mm');
  } catch {
    return value;
  }
}

function daysInRange(tc: Timecard | undefined, rangeStart: Date, rangeEnd: Date): TimecardDay[] {
  const interval = { start: rangeStart, end: rangeEnd };
  return (tc?.days ?? [])
    .filter((day) => {
      if (!day.date) return false;
      try {
        return isWithinInterval(parseISO(day.date), interval);
      } catch {
        return false;
      }
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function buildIndividualEmployeeExportPayload(input: {
  employeeId: number;
  employeeName: string;
  role: string;
  periodLabel: string;
  fromDate: string;
  toDate: string;
  rangeStart: Date;
  rangeEnd: Date;
  summaryMetrics: EmployeeStatItem[];
  timecards: Timecard[];
  payRecords: Record<number, PayRecord>;
  holidayRequests: HolidayRequest[];
  summary: DashboardSummary | null;
}): IndividualEmployeeExportPayload {
  const {
    employeeId,
    employeeName,
    role,
    periodLabel,
    fromDate,
    toDate,
    rangeStart,
    rangeEnd,
    summaryMetrics,
    timecards,
    payRecords,
    holidayRequests,
    summary,
  } = input;

  const tc = timecards.find((t) => t.employeeId === employeeId);
  const days = daysInRange(tc, rangeStart, rangeEnd);

  const dailyRows = days.map((day) => {
    const scheduled = getScheduledHours(day);
    const actual = getActualPaidHours(day);
    let overtime = 0;
    if (actual > scheduled && scheduled > 0) overtime += actual - scheduled;
    overtime += Number(day.extras) || 0;
    const rate = resolveWorkHourlyRate(payRecords[employeeId], {
      locationId: tc?.locationId,
      positionId: day.positionId,
      scheduleId: day.scheduleId,
    });
    const cost = rate * actual;
    const attended = day.scheduledIn ? (day.actualIn ? 'Yes' : 'No') : '—';
    const { lateClockInMins, earlyClockOutMins } = computeLateAndEarlyMinutes(day);
    return {
      day,
      scheduled,
      actual,
      overtime: round1(overtime),
      rate,
      cost: Math.round(cost * 100) / 100,
      attended,
      lateClockInMins,
      earlyClockOutMins,
    };
  });

  const holidayDays = holidayRequests
    .filter((h) => h.employeeId === employeeId && h.status?.toLowerCase() === 'approved')
    .flatMap((h) =>
      (h.holidayTypes ?? []).map((d) => ({
        date: d.date,
        holidayType: d.holidayType ?? h.leaveType?.name ?? 'Leave',
        leaveType: h.leaveType?.name ?? d.holidayType ?? 'Leave',
        status: h.status,
      })),
    )
    .filter((d) => {
      try {
        return isWithinInterval(parseISO(d.date), { start: rangeStart, end: rangeEnd });
      } catch {
        return false;
      }
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  const sickRows = holidayDays.filter((d) => d.holidayType.toLowerCase().includes('sick'));
  const annualRows = holidayDays.filter((d) => d.holidayType.toLowerCase().includes('annual'));

  const docRows: string[][] = [];
  (summary?.visaAlerts ?? [])
    .filter((v) => v.employeeId === employeeId)
    .forEach((v) => {
      docRows.push([
        'Visa',
        v.visaNumber || '—',
        formatShortDate(v.visaExpiryDate),
        String(v.daysUntilExpiry),
      ]);
    });
  (summary?.licenseExpired ?? [])
    .filter((l) => l.employeeId === employeeId)
    .forEach((l) => {
      docRows.push([
        `License (Expired)${l.licenseType ? ` – ${l.licenseType}` : ''}`,
        l.licenseNumber || '—',
        formatShortDate(l.expiryDate),
        String(-Math.abs(l.daysSinceExpiry)),
      ]);
    });
  (summary?.licenseExpiringSoon ?? [])
    .filter((l) => l.employeeId === employeeId)
    .forEach((l) => {
      docRows.push([
        `License${l.licenseType ? ` – ${l.licenseType}` : ''}`,
        l.licenseNumber || '—',
        formatShortDate(l.expiryDate),
        String(l.daysUntilExpiry),
      ]);
    });
  (summary?.upcomingTraining ?? [])
    .filter((t) => t.employeeId === employeeId)
    .forEach((t) => {
      docRows.push([
        `Training – ${t.label}`,
        t.isOverdue ? 'Overdue' : 'Upcoming',
        formatShortDate(t.dueDate),
        t.isOverdue ? 'Overdue' : 'Due soon',
      ]);
    });

  const detailByMetric: Record<string, EmployeeReportDetailSheet> = {
    'Scheduled Hours': {
      metricLabel: 'Scheduled Hours',
      title: 'Scheduled Hours',
      headers: ['Date', 'Scheduled In', 'Scheduled Out', 'Scheduled Hours', 'Schedule Code'],
      rows: dailyRows
        .filter((r) => r.scheduled > 0 || r.day.scheduledIn)
        .map((r) => [
          formatShortDate(r.day.date),
          formatTime(r.day.scheduledIn),
          formatTime(r.day.scheduledOut),
          String(round1(r.scheduled)),
          r.day.scheduleCode || '—',
        ]),
    },
    'Actual Hours': {
      metricLabel: 'Actual Hours',
      title: 'Actual Hours',
      headers: ['Date', 'Actual In', 'Actual Out', 'Actual Hours', 'Extras'],
      rows: dailyRows
        .filter((r) => r.actual > 0 || r.day.actualIn)
        .map((r) => [
          formatShortDate(r.day.date),
          formatTime(r.day.actualIn),
          formatTime(r.day.actualOut),
          String(round1(r.actual)),
          String(r.day.extras ?? 0),
        ]),
    },
    'Attendance %': {
      metricLabel: 'Attendance %',
      title: 'Attendance %',
      headers: ['Date', 'Scheduled', 'Clocked In', 'Attended', 'Late (min)', 'Early Leave (min)'],
      rows: dailyRows
        .filter((r) => !!r.day.scheduledIn)
        .map((r) => [
          formatShortDate(r.day.date),
          `${formatTime(r.day.scheduledIn)} – ${formatTime(r.day.scheduledOut)}`,
          formatTime(r.day.actualIn) || '—',
          r.attended,
          String(r.lateClockInMins),
          String(r.earlyClockOutMins),
        ]),
    },
    'Sick Days': {
      metricLabel: 'Sick Days',
      title: 'Sick Days',
      headers: ['Date', 'Leave Type', 'Status'],
      rows: sickRows.map((r) => [formatShortDate(r.date), r.holidayType, r.status]),
    },
    'Annual Leave': {
      metricLabel: 'Annual Leave',
      title: 'Annual Leave',
      headers: ['Date', 'Leave Type', 'Status'],
      rows: annualRows.map((r) => [formatShortDate(r.date), r.holidayType, r.status]),
    },
    Overtime: {
      metricLabel: 'Overtime',
      title: 'Overtime',
      headers: ['Date', 'Scheduled Hours', 'Actual Hours', 'Extras', 'Overtime Hours'],
      rows: dailyRows
        .filter((r) => r.overtime > 0)
        .map((r) => [
          formatShortDate(r.day.date),
          String(round1(r.scheduled)),
          String(round1(r.actual)),
          String(r.day.extras ?? 0),
          String(r.overtime),
        ]),
    },
    'Total Labour Cost': {
      metricLabel: 'Total Labour Cost',
      title: 'Total Labour Cost',
      headers: ['Date', 'Actual Hours', 'Hourly Rate', 'Cost (€)'],
      rows: dailyRows
        .filter((r) => r.cost > 0 || r.actual > 0)
        .map((r) => [
          formatShortDate(r.day.date),
          String(round1(r.actual)),
          r.rate.toFixed(2),
          r.cost.toFixed(2),
        ]),
    },
    'Documents Expiring': {
      metricLabel: 'Documents Expiring',
      title: 'Documents Expiring',
      headers: ['Type', 'Reference', 'Expiry / Due', 'Days'],
      rows: docRows,
    },
    'Incidents / Notes': {
      metricLabel: 'Incidents / Notes',
      title: 'Incidents / Notes',
      headers: ['Date', 'Note'],
      rows: tc?.notes
        ? [[formatShortDate(tc.weekStartDate), tc.notes]]
        : [],
    },
  };

  const detailSheets = summaryMetrics
    .map((m) => detailByMetric[m.label])
    .filter((sheet): sheet is EmployeeReportDetailSheet => !!sheet);

  return {
    employeeName,
    role,
    periodLabel,
    fromDate,
    toDate,
    summaryMetrics,
    detailSheets,
  };
}
